
import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Project } from '../data/projects';
import { X } from 'lucide-react';

interface ProjectModalProps {
    isOpen: boolean;
    onClose: () => void;
    project: Project | null;
}

const ProjectModal: React.FC<ProjectModalProps> = ({ isOpen, onClose, project }) => {
    const navigate = useNavigate();

    if (!project) return null;

    const handleViewDetails = () => {
        onClose();
        navigate(project.slug);
    };

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/80 z-[9990] backdrop-blur-sm"
                    />

                    {/* Modal Container */}
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 pointer-events-none">
                        <motion.div
                            layoutId={`project-${project.id}`} // Helper for shared element transition if we had one
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="bg-zinc-900 border border-white/10 w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl pointer-events-auto relative flex flex-col max-h-[90vh]"
                        >
                            {/* Close Button - Larger touch target for mobile */}
                            <button
                                onClick={onClose}
                                className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20 p-2 sm:p-2.5 bg-black/40 hover:bg-black/70 rounded-full text-white/90 hover:text-white transition-all backdrop-blur-sm"
                                aria-label="Close modal"
                            >
                                <X size={20} className="sm:w-6 sm:h-6" />
                            </button>

                            {/* Image Area */}
                            <div className="relative w-full h-56 sm:h-72 md:h-80 bg-zinc-800 shrink-0">
                                {project.mediaType === 'video' ? (
                                    <video
                                        src={project.thumbnailSrc}
                                        autoPlay muted loop playsInline
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <img
                                        src={project.modalImageSrc}
                                        alt={project.title}
                                        className="w-full h-full object-cover"
                                    />
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/40 to-transparent" />

                                <div className="absolute bottom-4 left-4 sm:bottom-6 sm:left-6 max-w-[85%]">
                                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-light text-white tracking-tight leading-tight">
                                        {project.title} <span className="text-white/60 block sm:inline text-xl sm:text-2xl md:text-3xl mt-1 sm:mt-0">{project.location}</span>
                                    </h2>
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        <div className="text-xs sm:text-sm font-medium px-3 py-1 bg-white/10 backdrop-blur-md rounded-full inline-block text-white/90 border border-white/10">
                                            {project.category.text}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Content Area */}
                            <div className="p-5 sm:p-8 overflow-y-auto flex flex-col h-full bg-zinc-900">
                                <p className="text-white/80 text-base sm:text-lg font-light leading-relaxed mb-8">
                                    {project.shortDescription}
                                </p>

                                <div className="mt-auto flex justify-end">
                                    <button
                                        onClick={handleViewDetails}
                                        className="group relative px-6 py-3 bg-white text-black rounded-lg font-medium transition-all hover:bg-gray-100 hover:shadow-lg hover:-translate-y-0.5 w-full sm:w-auto text-center flex items-center justify-center gap-2"
                                    >
                                        View Project Details
                                        <span className="group-hover:translate-x-1 transition-transform">→</span>
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default ProjectModal;
